import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { House, Plus, Trash, MapPin, Recycle } from "@phosphor-icons/react";

const BIN_TYPES = [
  { v: "restmuell", l: "Restmüll (schwarz)" },
  { v: "biomuell", l: "Biotonne (braun)" },
  { v: "papier", l: "Altpapier (blau)" },
  { v: "gelb", l: "Gelber Sack (gelb)" },
];

/** PropertiesSubPanel — mehrere Adressen pro Kunde */
export function PropertiesSubPanel({ customer, onChange }) {
  const [properties, setProperties] = useState(customer.properties || []);
  const [newProp, setNewProp] = useState({ label: "", street: "", zip: "", city: "" });
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const r = await api.get(`/customers/${customer.customer_id}/properties`);
    setProperties(r.data);
    onChange?.();
  };
  useEffect(() => { setProperties(customer.properties || []); }, [customer]);

  const add = async () => {
    if (!newProp.label || !newProp.street) return;
    setBusy(true);
    try {
      await api.post(`/customers/${customer.customer_id}/properties`, {
        label: newProp.label,
        location: { street: newProp.street, zip: newProp.zip, city: newProp.city },
      });
      setNewProp({ label: "", street: "", zip: "", city: "" });
      refresh();
    } finally { setBusy(false); }
  };
  const remove = async (pid) => {
    if (!confirm("Objekt wirklich löschen?")) return;
    await api.delete(`/customers/${customer.customer_id}/properties/${pid}`);
    refresh();
  };

  return (
    <div data-testid="properties-subpanel">
      <h4 className="font-semibold text-sm mb-2 flex items-center gap-2"><House size={16} weight="bold" /> Objekte / Adressen ({properties.length})</h4>
      <div className="space-y-1.5 mb-3 max-h-40 overflow-y-auto">
        {(properties || []).map((p) => (
          <div key={p.property_id} className="flex items-center justify-between text-xs p-2 border border-black/5 rounded-md" data-testid={`prop-${p.property_id}`}>
            <div className="flex items-center gap-2 min-w-0">
              <MapPin size={12} weight="bold" style={{ color: "var(--tonneva-primary)" }} />
              <div>
                <div className="font-semibold">{p.label}</div>
                <div className="text-zinc-500">{p.location?.street}, {p.location?.zip} {p.location?.city}</div>
              </div>
            </div>
            <button onClick={() => remove(p.property_id)} className="text-red-600 hover:bg-red-50 rounded p-1" data-testid={`prop-del-${p.property_id}`}><Trash size={12} weight="bold" /></button>
          </div>
        ))}
        {properties.length === 0 && <div className="text-xs text-zinc-400 py-2 text-center">Noch keine weiteren Objekte</div>}
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        <Input placeholder="Bezeichnung (z. B. Filiale)" className="col-span-4 h-9 text-xs rounded-md" value={newProp.label} onChange={(e) => setNewProp({ ...newProp, label: e.target.value })} data-testid="prop-new-label" />
        <Input placeholder="Straße & Nr" className="col-span-2 h-9 text-xs rounded-md" value={newProp.street} onChange={(e) => setNewProp({ ...newProp, street: e.target.value })} data-testid="prop-new-street" />
        <Input placeholder="PLZ" className="h-9 text-xs rounded-md" value={newProp.zip} onChange={(e) => setNewProp({ ...newProp, zip: e.target.value })} data-testid="prop-new-zip" />
        <Input placeholder="Stadt" className="h-9 text-xs rounded-md" value={newProp.city} onChange={(e) => setNewProp({ ...newProp, city: e.target.value })} data-testid="prop-new-city" />
      </div>
      <Button size="sm" className="mt-2 rounded-md text-xs h-8 text-white" style={{ background: "var(--tonneva-gradient)" }} onClick={add} disabled={busy || !newProp.label || !newProp.street} data-testid="prop-add">
        <Plus size={12} weight="bold" className="mr-1" /> Objekt hinzufügen
      </Button>
    </div>
  );
}

/** BinsSubPanel — Tonnen zum Kunden verwalten (Typ, Größe, Dauer, Barcode, NFC) */
export function BinsSubPanel({ customer, onChange }) {
  const [bins, setBins] = useState([]);
  const [nb, setNb] = useState({ bin_type: "restmuell", size_liter: 120, quantity: 1, duration_min: 3, label: "" });
  const [busy, setBusy] = useState(false);

  const loadBins = useCallback(async () => {
    const r = await api.get("/bins");
    setBins(r.data.filter((b) => b.customer_id === customer.customer_id));
  }, [customer.customer_id]);
  const refresh = async () => {
    await loadBins();
    onChange?.();
  };
  useEffect(() => { loadBins(); }, [loadBins]);

  const add = async () => {
    setBusy(true);
    try {
      await api.post("/bins", { customer_id: customer.customer_id, ...nb });
      setNb({ bin_type: "restmuell", size_liter: 120, quantity: 1, duration_min: 3, label: "" });
      refresh();
    } finally { setBusy(false); }
  };
  const remove = async (bid) => {
    if (!confirm("Tonne wirklich entfernen?")) return;
    await api.delete(`/bins/${bid}`);
    refresh();
  };
  const patchBin = async (bid, patch) => {
    await api.patch(`/bins/${bid}`, patch);
    refresh();
  };

  return (
    <div data-testid="bins-subpanel">
      <h4 className="font-semibold text-sm mb-2 flex items-center gap-2"><Recycle size={16} weight="bold" /> Tonnen ({bins.length})</h4>
      <div className="space-y-1.5 mb-3 max-h-52 overflow-y-auto">
        {bins.map((b) => (
          <div key={b.bin_id} className="text-xs p-2 border border-black/5 rounded-md" data-testid={`bin-${b.bin_id}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="font-semibold">{BIN_TYPES.find((t) => t.v === b.bin_type)?.l || b.bin_type} · {b.size_liter}L{b.quantity > 1 && ` × ${b.quantity}`}{b.label && ` — ${b.label}`}</div>
              <button onClick={() => remove(b.bin_id)} className="text-red-600 hover:bg-red-50 rounded p-1" data-testid={`bin-del-${b.bin_id}`}><Trash size={12} weight="bold" /></button>
            </div>
            <div className="grid grid-cols-3 gap-1.5 text-[10px] text-zinc-600 items-center">
              <div>Dauer: <input type="number" min="1" max="30" defaultValue={b.duration_min} onBlur={(e) => patchBin(b.bin_id, { duration_min: parseInt(e.target.value) })} className="w-12 border border-black/10 rounded px-1 py-0.5 text-xs" data-testid={`bin-duration-${b.bin_id}`} /> Min</div>
              <div>Barcode: <input type="text" placeholder="—" defaultValue={b.barcode || ""} onBlur={(e) => patchBin(b.bin_id, { barcode: e.target.value || null })} className="w-28 border border-black/10 rounded px-1 py-0.5 text-xs" data-testid={`bin-barcode-${b.bin_id}`} /></div>
              <div>NFC-UID: <input type="text" placeholder="—" defaultValue={b.nfc_uid || ""} onBlur={(e) => patchBin(b.bin_id, { nfc_uid: e.target.value || null })} className="w-24 border border-black/10 rounded px-1 py-0.5 text-xs font-mono" data-testid={`bin-nfc-${b.bin_id}`} /></div>
            </div>
          </div>
        ))}
        {bins.length === 0 && <div className="text-xs text-zinc-400 py-2 text-center">Keine Tonnen erfasst</div>}
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        <Select value={nb.bin_type} onValueChange={(v) => setNb({ ...nb, bin_type: v })}>
          <SelectTrigger className="h-9 text-xs rounded-md col-span-2" data-testid="bin-new-type"><SelectValue /></SelectTrigger>
          <SelectContent>{BIN_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="number" min="60" max="1100" placeholder="Liter" className="h-9 text-xs rounded-md" value={nb.size_liter} onChange={(e) => setNb({ ...nb, size_liter: parseInt(e.target.value) || 120 })} data-testid="bin-new-size" />
        <Input type="number" min="1" max="20" placeholder="Menge" className="h-9 text-xs rounded-md" value={nb.quantity} onChange={(e) => setNb({ ...nb, quantity: parseInt(e.target.value) || 1 })} data-testid="bin-new-qty" />
        <Input type="number" min="1" max="30" placeholder="Min" className="h-9 text-xs rounded-md" value={nb.duration_min} onChange={(e) => setNb({ ...nb, duration_min: parseInt(e.target.value) || 3 })} data-testid="bin-new-duration" />
      </div>
      <Button size="sm" className="mt-2 rounded-md text-xs h-8 text-white" style={{ background: "var(--tonneva-gradient)" }} onClick={add} disabled={busy} data-testid="bin-add">
        <Plus size={12} weight="bold" className="mr-1" /> Weitere Tonne hinzufügen
      </Button>
    </div>
  );
}
